import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { v4 as uuid } from 'uuid';
import {
  Button,
  Icon,
  Input,
  message,
  Modal,
  Popconfirm,
  Popover,
  Select,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { validate as uuidValidate } from 'uuid';

import {
  BlModalContainer,
  BusinessLogicContainer1,
  BusinessLogicSaveButton1,
  HorPopover,
  RuleEditingContainer1,
  TableEditContainer1,
} from './styles';
import { deepCopy } from '../../../utils/javascript';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { EQUALS_OPERATORS, OPERATOR_SYMBOLS, OPERATOR_VALUES } from '../BusinessLogicTab';
import { BUSINESS_LOGIC_TAB } from '../translationValues';
import {
  BusinessLogicFooter,
  DisableContainer,
  Placeholder,
  StyledSelect,
  TableLogicalOperator,
  TableOperator,
} from '../BusinessLogicTab/styles';
import { dummyCallback } from '../../../utils/complexDocumentConstants';
import { AddIcon, minusIcon } from '../../../images/svgIcons';
import { PurpleTooltip } from '../BusinessLogicTab/businessLogicTable';
import { updateInsertBusinessLogic } from '../../../utils/services/insertsApi';
import { INSERTS_TAB } from './translationValues';
import { ENTERPRISE_ENV } from '../../../utils/helper';
import { useSessionContext } from '../../../context/sessionContext';
import { generateTextFromPrompt } from '../../../utils/services/businessLogicApi';

const Option = Select.Option;

const NAMESPACES = ['BUSINESS_LOGIC'];
const EXCLUDED_VARIABLE_TYPES = ['ListString', 'ListDate'];
export const getDefaultVariable = () => {
  return {
    variableId: uuid(),
    type: '',
    isNew: true,
  };
};

export const getDefaultExtraCondition = () => {
  return {
    id: uuid(),
    operator: OPERATOR_VALUES.EQUALS,
    value: '',
  };
};

export const getDefaultCondition = (variableId = '') => {
  return {
    id: uuid(),
    operator: OPERATOR_VALUES.EQUALS,
    value: '',
    variableId: variableId,
    isNew: true,
    extraConditions: [],
  };
};

export const getDefaultRule = defaultConditions => {
  return {
    conditions: defaultConditions || [getDefaultCondition()],
    isNew: true,
    ruleText: '',
    expression: '',
  };
};
const newBusinessLogic = (name, documentId) => {
  return {
    id: uuid(),
    variables: [getDefaultVariable()],
    ...getDefaultRule(),
  };
};

const variableColors = {
  blue: '#0000ff',
  blueviolet: '#8a2be2',
  brown: '#a52a2a',
  crimson: '#dc143c',
  darkblue: '#00008b',
  darkgreen: '#006400',
  darkmagenta: '#8b008b',
  darkorange: '#ff8c00',
  darkred: '#8b0000',
  darkviolet: '#9400d3',
};
const allKeys = Object.keys(variableColors);
const numOfColors = allKeys.length - 1;

export const BusinessLogicPopoverInfo = ({ insert, globalVariables }) => {
  const { businessLogic } = insert;
  const { conditions } = businessLogic;
  let colorIndex = 0;

  const getRandomColor = () => {
    if (colorIndex > numOfColors) {
      colorIndex = 0;
    }
    const randomColor = variableColors[allKeys[colorIndex]];
    colorIndex++;
    return randomColor;
  };

  const generateExpressions = (conditions, masterVariableId, prevConValid = false) => {
    let promptExpression = [];
    conditions.forEach((condition, index) => {
      const { extraConditions, variableId, operator, value } = condition;
      const isExtraCondition = !!(extraConditions && extraConditions.length);
      const varId = masterVariableId || variableId;
      if (value) {
        if (index !== 0 && !masterVariableId && prevConValid) {
          promptExpression.push(<span>{' and '}</span>);
        }
        if (isExtraCondition) {
          promptExpression.push(<span>{'('}</span>);
        }
        if (masterVariableId) {
          if (!prevConValid) {
            promptExpression.push(<span>{' and ('}</span>);
          } else {
            promptExpression.push(<span>{' or '}</span>);
          }
        }
        const variable = globalVariables?.find(variable => variable.id === varId);
        const variableName = variable?.placeHolderText;
        if (EQUALS_OPERATORS.includes(operator)) {
          promptExpression.push(
            <span style={{ color: getRandomColor() }}>{`${variableName}`}</span>,
          );
          promptExpression.push(<span>{` ${operator} ${value}`}</span>);
        } else if (operator === OPERATOR_VALUES.EQUALS_ANY_OF) {
          const result = value
            .split(',')
            .map(v => `${v}`)
            .join(',');
          promptExpression.push(
            <span style={{ color: getRandomColor() }}>{`${variableName}`}</span>,
          );
          promptExpression.push(<span>{` ${operator} [${result}]`}</span>);
        } else {
          promptExpression.push(
            <span style={{ color: getRandomColor() }}>{`${variableName}`}</span>,
          );
          promptExpression.push(<span>{` ${operator} ${value}`}</span>);
        }
        prevConValid = true;
      } else {
        prevConValid = false;
      }

      if (isExtraCondition) {
        const extPExp = generateExpressions(extraConditions, variableId, prevConValid);
        if (extPExp) {
          promptExpression = [...promptExpression, ...extPExp];
          promptExpression.push(<span>{`)`}</span>);
        }
      }
    });
    return promptExpression;
  };

  const promptExpression = generateExpressions(conditions);
  return <div>{promptExpression}</div>;
};

const businessLogicInsertSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" id="think">
    <g>
      <path d="M12.5 2C8.58 2 5.427 4.808 5.011 8.627l-1 4.771A.5.5 0 0 0 4.5 14h1.009c.159.214.322.41.491.594v.906C6 16.879 7.122 18 8.5 18H10v3.5a.5.5 0 0 0 .5.5h6a.5.5 0 0 0 .5-.5v-6.005A7.511 7.511 0 0 0 20 9.5C20 5.364 16.636 2 12.5 2zm3.714 12.83a.501.501 0 0 0-.214.41V21h-5v-3.5a.5.5 0 0 0-.5-.5h-2c-.827 0-1.5-.673-1.5-1.5v-1.105a.5.5 0 0 0-.143-.35 6.486 6.486 0 0 1-.683-.832.5.5 0 0 0-.409-.213h-.65l.882-4.216C6.364 5.433 9.099 3 12.5 3 16.084 3 19 5.916 19 9.5a6.512 6.512 0 0 1-2.786 5.33z"></path>
      <path d="M15.5 9h-.551a2.485 2.485 0 0 0-.472-1.013l.376-.376a.5.5 0 0 0-.707-.707l-.426.427a2.44 2.44 0 0 0-.72-.28V6.5a.5.5 0 0 0-1 0v.551a2.462 2.462 0 0 0-.876.365l-.512-.512a.5.5 0 0 0-.707.707l.512.512a2.495 2.495 0 0 0-.366.877H9.5a.5.5 0 0 0 0 1h.555c.092.44.3.854.611 1.188v.312a1.83 1.83 0 0 0 1.333 1.756v.244a.5.5 0 0 0 1 0v-.244a1.83 1.83 0 0 0 1.333-1.756v-.312c.311-.335.52-.748.611-1.188h.557a.5.5 0 0 0 0-1zm-2.001 1.608a.502.502 0 0 0-.166.372v.52a.826.826 0 0 1-.433.717c-.089-.128-.231-.217-.4-.217s-.311.089-.401.217a.828.828 0 0 1-.433-.717v-.52a.499.499 0 0 0-.166-.372c-.322-.29-.5-.683-.5-1.108 0-.827.673-1.5 1.5-1.5s1.5.673 1.5 1.5c0 .425-.178.818-.501 1.108z"></path>
    </g>
  </svg>
);

export const BusinessLogicOptionPopover = ({
  showBlModal,
  deleteBusinessLogic,
  insert,
  globalVariables,
  t,
}) => {
  const { businessLogic } = insert;
  const [showPrimaryPopover, setShowPrimaryPopover] = useState(false);
  const [showSecondaryPopover, setShowSecondaryPopover] = useState(false);

  const verticalPopoverContent = (
    <div>
      {businessLogic?.ruleText ? (
        businessLogic.ruleText
      ) : (
        <BusinessLogicPopoverInfo insert={insert} globalVariables={globalVariables} />
      )}
    </div>
  );

  const onSuccess = () => {
    setShowSecondaryPopover(false);
  };

  const horizontalPopoverContent = (
    <HorPopover>
      <Tooltip
        title={
          !!businessLogic
            ? t(
                INSERTS_TAB.EDIT_BUSINESS_LOGIC_LABEL.key,
                INSERTS_TAB.EDIT_BUSINESS_LOGIC_LABEL.value,
              )
            : t(
                INSERTS_TAB.ADD_BUSINESS_LOGIC_LABEL.key,
                INSERTS_TAB.ADD_BUSINESS_LOGIC_LABEL.value,
              )
        }
        placement="bottom"
      >
        <Button
          onClick={() => {
            showBlModal(true);
            setShowPrimaryPopover(false);
          }}
        >
          <Icon type={!!businessLogic ? 'edit' : 'plus'} />
        </Button>
      </Tooltip>
      {!!businessLogic ? (
        <Tooltip
          title={t(
            INSERTS_TAB.DELETE_BUSINESS_LOGIC_LABEL.key,
            INSERTS_TAB.DELETE_BUSINESS_LOGIC_LABEL.value,
          )}
          placement="bottom"
        >
          <Popconfirm
            trigger="click"
            placement="top"
            title={t(
              INSERTS_TAB.DELETE_INSERT_BUSINESS_LOGIC_QUESTION_LABEL.key,
              INSERTS_TAB.DELETE_INSERT_BUSINESS_LOGIC_QUESTION_LABEL.value,
            )}
            onConfirm={() => deleteBusinessLogic(onSuccess)}
            cancelText={t(INSERTS_TAB.NO_LABEL.key, INSERTS_TAB.NO_LABEL.value)}
            okText={t(INSERTS_TAB.YES_LABEL.key, INSERTS_TAB.YES_LABEL.value)}
          >
            <Button>
              <Icon type="delete" />
            </Button>
          </Popconfirm>
        </Tooltip>
      ) : null}
      {!!businessLogic && (
        <>
          <Tooltip
            title={t(
              INSERTS_TAB.VIEW_BUSINESS_LOGIC_LABEL.key,
              INSERTS_TAB.VIEW_BUSINESS_LOGIC_LABEL.value,
            )}
            placement="bottom"
          >
            <Popover
              content={verticalPopoverContent}
              trigger="click"
              placement="top"
              overlayClassName="bl-vertical-popover"
              visible={showSecondaryPopover}
              onVisibleChange={vis => setShowSecondaryPopover(vis)}
            >
              <Button>
                <Icon type="eye" />
              </Button>
            </Popover>
          </Tooltip>
        </>
      )}
    </HorPopover>
  );

  return (
    <Tooltip
      title={t(INSERTS_TAB.BUSINESS_LOGIC_LABEL.key, INSERTS_TAB.BUSINESS_LOGIC_LABEL.value)}
      placement="bottom"
    >
      <Popover
        content={horizontalPopoverContent}
        trigger="click"
        overlayClassName="bl-hor-popover"
        visible={showPrimaryPopover}
        onVisibleChange={vis => {
          setShowPrimaryPopover(vis);
          if (!vis) {
            setShowSecondaryPopover(false);
          }
        }}
      >
        <Icon component={businessLogicInsertSvg} />
      </Popover>
    </Tooltip>
  );
};

const TableDraggadbleHead = ({ item, index, t, ruleHintTooltipProps, ...props }) => {
  return <>{index === 0 ? <th>{item}</th> : <th id={`rule-${index}`}>{item}</th>}</>;
};

export const TableHead = ({ tableHeadData = [], flowHints, isNewFlow, t, ...props }) => {
  return (
    <thead>
      <tr>
        {tableHeadData.map((item, index) => (
          <TableDraggadbleHead item={item} index={index} t={t} />
        ))}
      </tr>
    </thead>
  );
};

const updateTableColumnHeight = () => {
  const trs = document.querySelectorAll('div.table-container > table > tbody > tr');
  trs &&
    trs.forEach(tr => {
      const { firstChild, clientHeight } = tr;
      firstChild.style.height = clientHeight + 'px';
    });
};

const TableTags = ({ rowItem, extraItem, updateOperatorByKey }) => {
  const item = extraItem || rowItem;
  const tags = item.value ? item.value.split(',') : [];
  const handleClose = tagIndex => {
    const newTags = tags.filter((item, index) => index !== tagIndex).join(',');
    updateOperatorByKey(newTags, rowItem, 'value', extraItem);
  };
  return tags.map((tag, index) => (
    <Tag closable onClose={() => handleClose(index)} key={tag + index}>
      {tag}
    </Tag>
  ));
};

export const TableMainBody = ({ tableBodyData = [], globalVariables, isNewFlow, t, ...props }) => {
  const {
    updateVariableWithKey,
    updateOperatorByKey,
    setRuleDeleteIndex,
    flowHints,
    updateFlowHints,
    addExtraCondition,
    deleteExtraCondition,
    deleteCondition,
  } = props;

  const [variableHintProps, setVariableHintProps] = useState({});

  useEffect(() => updateTableColumnHeight(), [tableBodyData]);

  useEffect(() => {
    let showTimeout;
    if (isNewFlow) {
      showTimeout = setTimeout(() => setVariableHintProps({ visible: true }), 3500);
    }
    return () => {
      clearTimeout(showTimeout);
    };
  }, []);

  const variablePlaceholderClick = (value, variableItem, key) => {
    updateVariableWithKey(value, variableItem, key);
    const timeout = setTimeout(() => {
      const variableNode = document.getElementById(
        `${variableItem.variableId}-rule-condition-select`,
      );
      variableNode && variableNode.click();
      clearTimeout(timeout);
    }, 100);
  };

  const handleVariableSelect = (value, rowItem, bodyIndex) => {
    const { key } = value;
    updateVariableWithKey(key, rowItem, 'variableId');
    const varType = globalVariables.find(v => v.id === key)?.type?.toLowerCase();
    updateVariableWithKey(varType, rowItem, 'type');
    if (isNewFlow) {
      updateFlowHints({ ...flowHints, variableHintDone: true });
      updateOperatorByKey(false, tableBodyData[0][1], 'isNew');
      const hideTimeout = setTimeout(() => {
        setVariableHintProps({ visible: false });
        setVariableHintProps({});
        clearTimeout(hideTimeout);
      }, 1);
    }
    tableBodyData[bodyIndex].forEach((item, index) =>
      index !== 0 ? updateOperatorByKey(key, item, 'variableId') : null,
    );
  };

  const onMouseHoverRow = event => {
    const { clientHeight, lastChild } = event.currentTarget;
    // height of the parent tr divided by 2 and subtract 12 pixels
    // which is the radius of the subtract icon to position the element at center.
    lastChild.style.marginTop = clientHeight / 2 - 12 + 'px';
  };

  //handle enter or blur  for 'equals any of' input.
  const handleEnterOrBlur = (event, rowItem, extraItem) => {
    if ((event.which === 13 || event.type === 'blur') && event.target.value) {
      const item = extraItem || rowItem;
      const isTagAlreadyExists = item.value.split(',').includes(event.target.value);
      if (!isTagAlreadyExists) {
        const previousValue = item.value ? `${item.value},` : '';
        const value = previousValue + event.target.value;
        updateOperatorByKey(value, rowItem, 'value', extraItem);
        event.target.value = '';
      } else {
        message.error(
          t(BUSINESS_LOGIC_TAB.UNIQUE_VALUE_ERROR.key, BUSINESS_LOGIC_TAB.UNIQUE_VALUE_ERROR.value),
        );
      }
    }
  };

  return tableBodyData.map((bodyItem, bodyIndex) => {
    return (
      <tr onMouseEnter={onMouseHoverRow}>
        {bodyItem.map((rowItem, rowIndex) => {
          if (rowIndex === 0) {
            const { variableId, isNew } = rowItem;
            const name = globalVariables?.find(gVar => gVar.id === variableId)?.placeHolderText;
            return (
              <td>
                <div className="condition">
                  <PurpleTooltip
                    overlay={t(
                      BUSINESS_LOGIC_TAB.CONDITION_SELECT_TOOLTIP_TEXT.key,
                      BUSINESS_LOGIC_TAB.CONDITION_SELECT_TOOLTIP_TEXT.value,
                    )}
                    placement="top"
                    {...variableHintProps}
                  >
                    {!isNew ? (
                      <StyledSelect
                        value={name ? { key: variableId, label: name } : undefined}
                        id={`${variableId}-rule-condition-select`}
                        showSearch={true}
                        showArrow={false}
                        dropdownMatchSelectWidth={false}
                        placeholder={t(
                          BUSINESS_LOGIC_TAB.BL_VARIABLE_PLACEHOLDER.key,
                          BUSINESS_LOGIC_TAB.BL_VARIABLE_PLACEHOLDER.value,
                        )}
                        labelInValue
                        style={{ minWidth: !name ? '150px' : undefined }}
                        onChange={value => handleVariableSelect(value, rowItem, bodyIndex)}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {globalVariables.map(gVariable => (
                          <Option value={gVariable.id}>{gVariable.placeHolderText}</Option>
                        ))}
                      </StyledSelect>
                    ) : (
                      <Placeholder
                        onClick={() => variablePlaceholderClick(false, rowItem, 'isNew')}
                      >
                        {isNewFlow && !flowHints.variableHintDone ? (
                          t(
                            BUSINESS_LOGIC_TAB.BL_VARIABLE_TEXT.key,
                            BUSINESS_LOGIC_TAB.BL_VARIABLE_TEXT.value,
                          )
                        ) : (
                          <Icon type="plus" />
                        )}
                      </Placeholder>
                    )}
                  </PurpleTooltip>
                </div>
              </td>
            );
          }

          const { isNew, operator, value, extraConditions } = rowItem;
          if (isNew) {
            if (isNewFlow && !flowHints.variableHintDone) {
              return (
                <td
                  onMouseEnter={() => setRuleDeleteIndex(rowIndex)}
                  onMouseLeave={() => setRuleDeleteIndex(0)}
                ></td>
              );
            }
          }

          return (
            <td
              onMouseEnter={() => setRuleDeleteIndex(rowIndex)}
              onMouseLeave={() => setRuleDeleteIndex(0)}
            >
              <TableOperator>
                <Select
                  value={operator}
                  onChange={value => updateOperatorByKey(value, rowItem, 'operator')}
                  placeholder={t(
                    BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.key,
                    BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.value,
                  )}
                  dropdownMatchSelectWidth={false}
                  suffixIcon={<Icon type="caret-down" />}
                >
                  {Object.keys(OPERATOR_VALUES).map(key => (
                    <Option value={OPERATOR_VALUES[key]}>{OPERATOR_VALUES[key]}</Option>
                  ))}
                </Select>
                {operator !== OPERATOR_VALUES.EQUALS_ANY_OF ? (
                  operator !== OPERATOR_VALUES.IS_EMPTY ?
                    <Input
                      value={value}
                      onChange={event => updateOperatorByKey(event.target.value, rowItem, 'value')}
                      placeholder={t(
                        BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                        BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                      )}
                    />
                  : <Input value={''} disabled />
                ) : (
                  <input
                    id="tag-input"
                    onKeyPress={event => handleEnterOrBlur(event, rowItem)}
                    onBlur={event => handleEnterOrBlur(event, rowItem)}
                    placeholder={t(
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                      BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                    )}
                  />
                )}
              </TableOperator>
              {operator === OPERATOR_VALUES.EQUALS_ANY_OF ? (
                <TableTags rowItem={rowItem} updateOperatorByKey={updateOperatorByKey} />
              ) : (
                <></>
              )}
              {extraConditions.map(extraItem => {
                const { operator: extraOperator, value: extraValue } = extraItem;
                return (
                  <>
                    <TableLogicalOperator>---or---</TableLogicalOperator>
                    <TableOperator>
                      <Select
                        value={extraOperator}
                        onChange={value =>
                          updateOperatorByKey(value, rowItem, 'operator', extraItem)
                        }
                        placeholder={t(
                          BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.key,
                          BUSINESS_LOGIC_TAB.OPERATOR_PLACEHOLDER.value,
                        )}
                        dropdownMatchSelectWidth={false}
                        suffixIcon={<Icon type="caret-down" />}
                      >
                        {Object.keys(OPERATOR_VALUES).map(key => (
                          <Option value={OPERATOR_VALUES[key]}>{OPERATOR_VALUES[key]}</Option>
                        ))}
                      </Select>
                      {extraOperator !== OPERATOR_VALUES.EQUALS_ANY_OF ? (
                        extraOperator !== OPERATOR_VALUES.IS_EMPTY ?
                          <Input
                            value={extraValue}
                            onChange={event =>
                              updateOperatorByKey(event.target.value, rowItem, 'value', extraItem)
                            }
                            placeholder={t(
                              BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                              BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                            )}
                          />
                        : <Input value={''} disabled />
                      ) : (
                        <input
                          id="tag-input"
                          onKeyPress={event => handleEnterOrBlur(event, rowItem, extraItem)}
                          onBlur={event => handleEnterOrBlur(event, rowItem, extraItem)}
                          placeholder={t(
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.key,
                            BUSINESS_LOGIC_TAB.VALUE_LABEL.value,
                          )}
                        />
                      )}
                      <Popconfirm
                        title={t(
                          BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.key,
                          BUSINESS_LOGIC_TAB.DELETE_BL_CONFIRM.value,
                        )}
                        okText={t(
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.key,
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_YES.value,
                        )}
                        cancelText={t(
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.key,
                          BUSINESS_LOGIC_TAB.BL_CONFIRM_NO.value,
                        )}
                        onConfirm={() => deleteExtraCondition(rowItem, extraItem)}
                      >
                        <Tooltip
                          title={t(
                            BUSINESS_LOGIC_TAB.DELETE_CONDITION.key,
                            BUSINESS_LOGIC_TAB.DELETE_CONDITION.value,
                          )}
                        >
                          <Icon type="delete" style={{ alignSelf: 'center' }} />
                        </Tooltip>
                      </Popconfirm>
                    </TableOperator>
                    {extraOperator === OPERATOR_VALUES.EQUALS_ANY_OF ? (
                      <TableTags
                        rowItem={rowItem}
                        extraItem={extraItem}
                        updateOperatorByKey={updateOperatorByKey}
                      />
                    ) : (
                      <></>
                    )}
                  </>
                );
              })}
              <div>
                <span
                  onClick={() => addExtraCondition(rowItem)}
                  style={{ fontSize: '12px', cursor: 'pointer' }}
                >
                  <Icon type="plus" />{' '}
                  {t(BUSINESS_LOGIC_TAB.ADD_MORE.key, BUSINESS_LOGIC_TAB.ADD_MORE.value)}
                </span>
              </div>
            </td>
          );
        })}
        {tableBodyData.length > 1 ? (
          <PurpleTooltip
            title={t(
              BUSINESS_LOGIC_TAB.REMOVE_CONDITION_TOOLTIP.key,
              BUSINESS_LOGIC_TAB.REMOVE_CONDITION_TOOLTIP.value,
            )}
          >
            <div>
              <Icon
                onClick={() => deleteCondition(bodyIndex, tableBodyData[0][1])}
                component={minusIcon}
              />
            </div>
          </PurpleTooltip>
        ) : (
          <></>
        )}
      </tr>
    );
  });
};

export const InsertBusinessLogicModal = ({
  insert,
  templateInserts,
  visible,
  onCancel,
  showBlModal,
}) => {
  const { t } = useTranslation(NAMESPACES);
  const { businessLogic = newBusinessLogic() } = insert;
  const { variables = [], id, ...rule } = businessLogic;
  const [saveStatus, setSaveStatus] = useState('');
  const [variablesList, setVariablesList] = useState(deepCopy(variables));
  const [rulesList, setRulesList] = useState(deepCopy([rule]));
  const artboardSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES],
    [],
  );
  const [globalVariables, store] = useDocumentContext(artboardSelector);
  const gVariablesWithoutList = useMemo(
    () => globalVariables.filter(gVariable => !EXCLUDED_VARIABLE_TYPES.includes(gVariable.type)),
    [globalVariables],
  );
  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);
  const saveButtonRef = useRef(null);
  const saveEnabled = !!rulesList[0]?.conditions[0]?.operator && (rulesList[0].conditions[0].operator === OPERATOR_VALUES.IS_EMPTY || !!rulesList[0].conditions[0].value);
  const disabled = saveStatus === 'saving';
  const editTableContainerClass = `table-container`;

  const isEditMode = true;
  const isNewFlow = false;
  const tableHeadData = [
    t(BUSINESS_LOGIC_TAB.CONDITIONS_LABEL.key, BUSINESS_LOGIC_TAB.CONDITIONS_LABEL.value),
    t(BUSINESS_LOGIC_TAB.RULE_LABEL.key, BUSINESS_LOGIC_TAB.RULE_LABEL.value),
  ];
  const tableBodyData = variablesList.map((variable, index) => [
    variable,
    ...rulesList.map(rule => ({
      ...rule.conditions[index],
      ruleId: rule.id,
    })),
  ]);

  const validateVariables = variables => {
    let isError = false;
    for (let i = 0; i < variables.length; i++) {
      if (uuidValidate(variables[i].variableId)) {
        message.error(
          t(
            BUSINESS_LOGIC_TAB.EMPTY_CONDITION_ERROR_MESSAGE.key,
            BUSINESS_LOGIC_TAB.EMPTY_CONDITION_ERROR_MESSAGE.value,
            { value: i + 1 },
          ),
        );
        isError = true;
        break;
      }
    }
    return isError;
  };

  const generateExpressions = (conditions, masterVariableId, prevConValid = false) => {
    let expression = '';
    let promptExpression = '';
    conditions.forEach((condition, index) => {
      const { extraConditions, variableId, operator, value } = condition;
      const isExtraCondition = !!(extraConditions && extraConditions.length);
      const varId = masterVariableId || variableId;
      if (value) {
        if (index !== 0 && !masterVariableId && prevConValid) {
          expression += ' && ';
          promptExpression += ' && ';
        }
        if (isExtraCondition) {
          expression += '(';
          promptExpression += '(';
        }
        if (masterVariableId) {
          if (!prevConValid) {
            expression += ' && (';
            promptExpression += ' && (';
          } else {
            expression += ' || ';
            promptExpression += ' || ';
          }
        }
        const variable = globalVariables?.find(variable => variable.id === varId);
        const variableId = variable?.id;
        const variableName = variable?.placeHolderText;
        if (EQUALS_OPERATORS.includes(operator)) {
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} '${value}'`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} '${value}'`;
        } else if (operator === OPERATOR_VALUES.EQUALS_ANY_OF) {
          const result = value
            .split(',')
            .map(v => `'${v}'`)
            .join(',');
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} [${result}]`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} [${result}]`;
        } else if(operator === OPERATOR_VALUES.IS_EMPTY) {
                    expression += `${variableId}.isBlank()`;
                    promptExpression += `${variableName} Is Empty`;
        } else if(operator === OPERATOR_VALUES.DOES_NOT_START_WITH) {
          expression += `!${variableId}.startsWith('${value}')`;
          promptExpression += `${variableName} Does not start with '${value}'`;
        } else {
          expression += `${variableId} ${OPERATOR_SYMBOLS[operator]} ${value}`;
          promptExpression += `${variableName} ${OPERATOR_SYMBOLS[operator]} ${value}`;
        }
        prevConValid = true;
      } else {
        prevConValid = false;
      }

      if (isExtraCondition) {
        const [extraExp, extPExp] = generateExpressions(extraConditions, variableId, prevConValid);
        if (extraExp) {
          expression += `${extraExp})`;
        }
        if (extPExp) {
          promptExpression += `${extPExp})`;
        }
      }
    });
    return [expression, promptExpression];
  };

  const processVariables = variables => {
    return variables.map(variable => {
      delete variable.isNew;
      return variable;
    });
  };

  const processConditions = conditions => {
    return conditions.map(condition => {
      delete condition.isNew;
      return condition;
    });
  };

  const generateTextFromExpression = async prompt => {
    const response = await generateTextFromPrompt(prompt);
    if (response.statusCode === 2201) {
      let str = response.data.choices[0].text;
      str = new DOMParser().parseFromString(str, 'text/html').documentElement.textContent;
      let dataString = str.split('\n');
      dataString = dataString.join('');
      return dataString;
    }
    return '';
  };


  const processRules = async rules => {
    const promises = [];
    let orderNumber = 1;
    const isAiWritingEnabled =
      ENTERPRISE_ENV && organisationDetails ? organisationDetails.aiWriting : true;
    rules = rules.map(rule => {
      // remove isNew flag before sending to API.
      delete rule.isNew;
      rule.conditions = processConditions(rule.conditions);

      // increment orderNumber
      rule.orderNumber = orderNumber;
      orderNumber += 1;

      //generate expression
      const [expression, promptExpression] = generateExpressions(rule.conditions);
      rule.expression = expression;

      if (promptExpression) {
        const prompt = promptExpression
          .replace(/\|\|/g, 'OR')
          .replace(/ && /g, ', ')
          .replace(/\(/g, '')
          .replace(/\)/g, '')
          .replace(/==/g, '=')
          .replace(/ in /g, ' = ')
          .replace(/'/g, '');

        if (isAiWritingEnabled) {
          const query = `Interpret a meaningful message and translate the following rules into a statement 
          in natural language starting with "When: ${prompt} Then: show ${insert.name}"`;
          promises.push(generateTextFromExpression(query));
        } else {
          promises.push(Promise.resolve(''));
        }
      } else {
        promises.push(Promise.resolve(''));
      }

      return rule;
    });

    const generatedTextList = await Promise.allSettled(promises);
    rules = rules.map((rule, index) => {
      rule.ruleText = generatedTextList[index].value;
      return rule;
    });

    return rules;
  };

  const onSave = async () => {
    //validate before save
    if (validateVariables(variablesList)) {
      return;
    }
    setSaveStatus('saving');
    const variables = processVariables(variablesList);
    const rules = await processRules(rulesList);
    const newBusinessLogic = {
      ...rules[0],
      variables: variables,
      id: uuidValidate(id) ? null : id,
    };
    try {
      const payload = {
        relationId: insert.relationId,
        businessLogic: newBusinessLogic,
      };
      const response = await updateInsertBusinessLogic(payload);
      if (response?.statusCode === 132) {
        setSaveStatus('saved');
        const tempInserts = deepCopy(templateInserts).map(tInsert => {
          if (tInsert.relationId === insert.relationId) {
            tInsert.businessLogic = {
              ...newBusinessLogic,
              id: newBusinessLogic.id || response?.data,
            };
          }
          return tInsert;
        });
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: tempInserts });
        const timeout = setTimeout(() => {
          setSaveStatus('');
          clearTimeout(timeout);
        }, 2000);
      } else {
        setSaveStatus('');
      }
    } catch (e) {
      message.error(
        t(BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.key, BUSINESS_LOGIC_TAB.SAVE_BL_ERROR.value),
      );
      setSaveStatus('');
    }
  };

  const addCondition = () => {
    const variable = getDefaultVariable();
    setVariablesList([...variablesList, variable]);
    setRulesList(
      rulesList.map(item => {
        item.conditions = [...item.conditions, getDefaultCondition()];
        return item;
      }),
    );
  };

  const deleteCondition = varIndex => {
    setVariablesList(variablesList.filter((varItem, index) => index !== varIndex));
    setRulesList(
      rulesList.map(rule => {
        rule.conditions = rule.conditions.filter((conItem, index) => {
          return varIndex !== index;
        });
        return rule;
      }),
    );
  };

  const addExtraCondition = operatorItem => {
    setRulesList(
      rulesList.map(rule => {
        if (rule.id === operatorItem.ruleId) {
          rule.conditions = rule.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              conItem.extraConditions = [...conItem.extraConditions, getDefaultExtraCondition()];
            }
            return conItem;
          });
        }
        return rule;
      }),
    );
  };

  const deleteExtraCondition = (operatorItem, extraOperatorItem) => {
    setRulesList(
      rulesList.map(rule => {
        if (rule.id === operatorItem.ruleId) {
          rule.conditions = rule.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              conItem.extraConditions = conItem.extraConditions.filter(
                item => item.id !== extraOperatorItem.id,
              );
            }
            return conItem;
          });
        }
        return rule;
      }),
    );
  };

  const updateVariableWithKey = (value, variableItem, key = 'variableId') => {
    setVariablesList(
      variablesList.map(varItem => {
        if (varItem.variableId === variableItem.variableId) {
          varItem[key] = value;
        }
        return varItem;
      }),
    );
  };

  // TODO: Redo this logic if and when performance gets hit.
  const updateOperatorByKey = (value, operatorItem, key, extraItem = null) => {
    setRulesList(
      rulesList.map(item => {
        if (item.id === operatorItem.ruleId) {
          item.conditions = item.conditions.map(conItem => {
            if (conItem.id === operatorItem.id) {
              if (extraItem) {
                conItem.extraConditions = conItem.extraConditions.map(exItem => {
                  if (exItem.id === extraItem.id) {
                    exItem[key] = value;
                  }
                  return exItem;
                });
              } else {
                conItem[key] = value;
              }
            }
            return conItem;
          });
        }
        return item;
      }),
    );
  };

  return (
    <BlModalContainer>
      <Modal
        title={null}
        centered
        visible={visible}
        width=""
        onCancel={onCancel}
        style={{ padding: '0px !important' }}
        footer={null}
        className="insertPreviewModalView"
        closable={true}
        getContainer={document.getElementById('modal-popup-insert-container')}
      >
        <div>
          <div className="add-edit-label">
            {insert?.businessLogic
              ? t(
                  BUSINESS_LOGIC_TAB.EDIT_BUSINESS_LOGIC_CAPS_LABEL.key,
                  BUSINESS_LOGIC_TAB.EDIT_BUSINESS_LOGIC_CAPS_LABEL.value,
                )
              : t(
                  BUSINESS_LOGIC_TAB.ADD_BUSINESS_LOGIC_CAPS_LABEL.key,
                  BUSINESS_LOGIC_TAB.ADD_BUSINESS_LOGIC_CAPS_LABEL.value,
                )}
          </div>
          <DisableContainer disabled={disabled}>
            {
              <>
                <BusinessLogicSaveButton1 show={saveEnabled && isEditMode} ref={saveButtonRef}>
                  {saveStatus === 'saved' ? (
                    <span>
                      {t(
                        BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.key,
                        BUSINESS_LOGIC_TAB.SUCCESSFULLY_SAVED.value,
                      )}
                    </span>
                  ) : (
                    <>
                      {saveStatus !== 'saving' ? (
                        <div onClick={onSave}>
                          {t(
                            BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.key,
                            BUSINESS_LOGIC_TAB.SAVE_THIS_LOGIC.value,
                          )}
                        </div>
                      ) : (
                        <Spin indicator={<Icon type="loading" />}></Spin>
                      )}
                    </>
                  )}
                </BusinessLogicSaveButton1>
              </>
            }
            <BusinessLogicContainer1 disabled={disabled} show={true} showEdit={isEditMode}>
              <RuleEditingContainer1>
                <TableEditContainer1 className={editTableContainerClass} show={isEditMode}>
                  <table>
                    <TableHead tableHeadData={tableHeadData} t={t} />
                    <tbody>
                      <TableMainBody
                        tableBodyData={tableBodyData}
                        variablesList={variablesList}
                        updateVariableWithKey={updateVariableWithKey}
                        updateOperatorByKey={updateOperatorByKey}
                        addExtraCondition={addExtraCondition}
                        deleteExtraCondition={deleteExtraCondition}
                        deleteCondition={deleteCondition}
                        setRuleDeleteIndex={dummyCallback}
                        globalVariables={gVariablesWithoutList}
                        t={t}
                        isNewFlow={isNewFlow}
                        flowHints={{}}
                        updateFlowHints={dummyCallback}
                      />
                    </tbody>
                  </table>
                </TableEditContainer1>
                {isEditMode ? (
                  <BusinessLogicFooter>
                    <div onClick={addCondition} className="new-condition">
                      <Icon component={AddIcon} />{' '}
                      <span>
                        {t(
                          BUSINESS_LOGIC_TAB.NEW_CONDITION.key,
                          BUSINESS_LOGIC_TAB.NEW_CONDITION.value,
                        )}
                      </span>
                    </div>
                  </BusinessLogicFooter>
                ) : (
                  <></>
                )}
              </RuleEditingContainer1>
            </BusinessLogicContainer1>
          </DisableContainer>
        </div>
      </Modal>
    </BlModalContainer>
  );
};
